import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Globe, CheckCircle, Plus, Trash2, X, Users, Pencil, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'fi', label: 'Suomi' },
  { code: 'en', label: 'English' },
];

const ROLE_LABELS = { superadmin: 'Superadmin', admin: 'Admin' };

// Huolto-käyttäjän asiakkaat (#86): Superadmin valitsee yhden tai useamman.
function TenantChecklist({ tenants, selected, onChange }) {
  function toggle(id) {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  }
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {tenants.map(tn => (
        <label key={tn.id} className="flex items-center gap-1.5 text-sm text-gray-700">
          <input type="checkbox" checked={selected.includes(tn.id)} onChange={() => toggle(tn.id)} />
          {tn.name}
        </label>
      ))}
    </div>
  );
}

// Huolto-käyttäjän rivi: asiakkaat näkyvät, Superadmin muokkaa valintaa.
function HuoltoAccess({ user, tenants, canEdit, onSaved }) {
  const { t } = useTranslation();
  const current = (user.access_tenants || []).map(x => x.id);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    setSaving(true); setError('');
    try {
      await api.put(`/api/admin/users/${user.id}`, { tenant_ids: selected });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <p className="text-xs text-gray-500 mt-0.5 flex items-start gap-1.5">
        <span>{t('users.huoltoCustomers')}: {(user.access_tenants || []).map(x => x.name).join(', ') || '—'}</span>
        {canEdit && (
          <button onClick={() => { setSelected(current); setEditing(true); }} title={t('users.editCustomers')}
            className="text-gray-400 hover:text-brand-700 shrink-0">
            <Pencil size={12} />
          </button>
        )}
      </p>
    );
  }
  return (
    <div className="mt-2 space-y-2">
      <TenantChecklist tenants={tenants} selected={selected} onChange={setSelected} />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={saving || selected.length === 0}
          className="flex items-center gap-1 text-xs bg-brand-700 hover:bg-brand-900 text-white px-3 py-1 rounded-lg disabled:bg-gray-300 disabled:text-gray-600">
          <Check size={12} /> {t('common.save')}
        </button>
        <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:text-gray-800 px-2">{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function LanguageSection() {
  const { t, i18n } = useTranslation();
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleLanguageChange(code) {
    setLoading(true);
    setSaved(false);
    try {
      await api.patch('/api/admin/me/language', { language: code });
      localStorage.setItem('safepro_language', code);
      i18n.changeLanguage(code);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
        <Globe size={15} />{t('account.language')}
      </div>
      <div className="flex flex-wrap gap-2">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            disabled={loading}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-60 ${
              i18n.language === lang.code
                ? 'bg-brand-700 text-white border-brand-700'
                : 'bg-white text-gray-700 border-gray-300 hover:border-brand-700 hover:text-brand-700'
            }`}
          >
            {lang.label}
          </button>
        ))}
      </div>
      {saved && (
        <div className="flex items-center gap-2 text-green-700 text-sm mt-2">
          <CheckCircle size={13} />{t('account.languageSaved')}
        </div>
      )}
    </div>
  );
}

function UserManagementSection({ currentUserId, isSuperadmin, ownTenantSsoRequired }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', tenant_id: '', role: 'admin', tenant_ids: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/users');
      setUsers(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    if (isSuperadmin) {
      api.get('/api/superadmin/tenants').then(({ data }) => setTenants(data || []));
    }
  }, [loadUsers, isSuperadmin]);

  // SSO-asiakkaan käyttäjä kirjautuu Microsoft-tilillä, joten salasanaa ei kysytä (#28)
  // Huolto-käyttäjä (#86) kirjautuu aina salasanalla, eikä sillä ole kotiasiakasta.
  const isHuoltoForm = isSuperadmin && form.role === 'huolto';
  const ssoOnly = isHuoltoForm ? false : isSuperadmin
    ? Boolean(tenants.find(x => x.id === form.tenant_id)?.sso_required)
    : Boolean(ownTenantSsoRequired);

  function resetForm() {
    setForm({ email: '', password: '', tenant_id: '', role: 'admin', tenant_ids: [] });
    setError('');
    setShowForm(false);
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = isHuoltoForm
        ? { email: form.email, password: form.password, role: 'huolto', tenant_ids: form.tenant_ids }
        : { email: form.email, password: ssoOnly ? undefined : form.password, tenant_id: form.tenant_id, role: 'admin' };
      await api.post('/api/admin/users', body);
      resetForm();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || t('users.createFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(user) {
    if (!window.confirm(t('users.confirmDelete', { email: user.email }))) return;
    try {
      await api.delete(`/api/admin/users/${user.id}`);
      setUsers(u => u.filter(x => x.id !== user.id));
    } catch {
      // ignore
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Users size={15} />{t('users.tabUsers')}
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 text-xs text-brand-700 hover:text-brand-900 font-medium"
          >
            <Plus size={13} />{isSuperadmin ? t('users.createUser') : t('users.createAdmin')}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="px-5 py-4 border-b border-gray-100 bg-gray-50 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">{isSuperadmin ? t('users.newUser') : t('users.newAdminUser')}</span>
            <button type="button" onClick={resetForm} className="text-gray-400 hover:text-gray-700">
              <X size={15} />
            </button>
          </div>
          {isSuperadmin && (
            <div className="flex gap-4 text-sm text-gray-700" role="radiogroup" aria-label={t('users.role')}>
              {['admin', 'huolto'].map(r => (
                <label key={r} className="flex items-center gap-1.5">
                  <input type="radio" name="new-user-role" checked={form.role === r}
                    onChange={() => setForm(f => ({ ...f, role: r }))} />
                  {r === 'admin' ? t('users.roleAdmin') : t('users.roleHuolto')}
                </label>
              ))}
            </div>
          )}
          {isHuoltoForm && <p className="text-xs text-gray-500">{t('users.huoltoHelp')}</p>}
          <div className={`grid grid-cols-1 gap-2 ${isSuperadmin && !isHuoltoForm ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            <input
              type="email"
              placeholder={t('users.emailLabel')}
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
              className="rounded-lg border-gray-300 text-sm focus:ring-brand-600 focus:border-brand-600"
            />
            {ssoOnly ? (
              <p className="text-xs text-gray-500 self-center">{t('users.ssoNoPassword')}</p>
            ) : (
              <input
                type="password"
                placeholder={t('users.passwordLabel')}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                required
                className="rounded-lg border-gray-300 text-sm focus:ring-brand-600 focus:border-brand-600"
              />
            )}
            {isSuperadmin && !isHuoltoForm && (
              <select
                value={form.tenant_id}
                onChange={e => setForm(f => ({ ...f, tenant_id: e.target.value }))}
                required
                className="rounded-lg border-gray-300 text-sm focus:ring-brand-600 focus:border-brand-600"
              >
                <option value="">{t('users.selectCustomer')}</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
          </div>
          {isHuoltoForm && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">{t('users.huoltoCustomers')}</p>
              <TenantChecklist tenants={tenants} selected={form.tenant_ids} onChange={ids => setForm(f => ({ ...f, tenant_ids: ids }))} />
            </div>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={saving || (isHuoltoForm && form.tenant_ids.length === 0)}
            className="text-sm bg-brand-700 hover:bg-brand-900 text-white px-4 py-1.5 rounded-lg disabled:opacity-60"
          >
            {saving ? t('users.creating') : t('users.create')}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 px-5 py-4">{t('common.loading')}</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between px-5 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{u.email}</p>
                <p className="text-xs text-gray-400">
                  {u.role === 'huolto' ? t('users.roleHuolto') : (ROLE_LABELS[u.role] || u.role)}
                  {u.tenant_name ? ` · ${u.tenant_name}` : ''}
                  {u.phone ? ` · ${u.phone}` : ''}
                  {u.sso_linked ? ` · ${t('users.ssoLinked')}` : ''}
                </p>
                {u.role === 'huolto' && (
                  <HuoltoAccess user={u} tenants={tenants} canEdit={isSuperadmin} onSaved={loadUsers} />
                )}
              </div>
              {u.id !== currentUserId && (
                <button
                  onClick={() => handleDelete(u)}
                  className="text-gray-300 hover:text-red-600 p-1.5 rounded ml-3 shrink-0 transition-colors"
                  title={t('users.deleteUser')}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
          {users.length === 0 && (
            <p className="text-sm text-gray-400 px-5 py-4">{t('users.noUsers')}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function UsersPage() {
  const { profile } = useAuth();
  const isSuperadmin = profile?.role === 'superadmin';
  const isAdmin = profile?.role === 'admin';

  return (
    <div className="max-w-2xl space-y-6">
      {(isSuperadmin || isAdmin) && (
        <UserManagementSection
          currentUserId={profile?.id}
          isSuperadmin={isSuperadmin}
          ownTenantSsoRequired={profile?.tenant_sso_required}
        />
      )}
      <div className="max-w-md">
        <LanguageSection />
      </div>
    </div>
  );
}
